/**
 * Safely formats API values for `<input>` money fields.
 * Prisma `Decimal` often arrives as a plain object after JSON parse — `String(obj)` becomes `[object Object]`.
 */
export function formatMoneyFieldForForm(value: unknown): string {
  if (value == null || value === '') {
    return '';
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '';
  }
  if (typeof value === 'object' && value !== null) {
    return stringFromDecimalLike(value as Record<string, unknown>);
  }
  return '';
}

function stringFromDecimalLike(o: Record<string, unknown>): string {
  const withToFixed = o as { toString?: () => string; toFixed?: (fractionDigits?: number) => string };
  if (typeof withToFixed.toFixed === 'function') {
    const s = withToFixed.toString?.();
    if (typeof s === 'string' && s !== '[object Object]') {
      return s;
    }
  }
  const toStringFn = o.toString;
  if (typeof toStringFn === 'function' && toStringFn !== Object.prototype.toString) {
    const s = toStringFn.call(o);
    if (typeof s === 'string' && s !== '[object Object]') {
      return s;
    }
  }
  if (typeof o['$numberDecimal'] === 'string') {
    return o['$numberDecimal'];
  }
  if (typeof o['value'] === 'string') {
    return o['value'];
  }
  return '';
}

function finiteNumberFromString(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

function isPrismaDecimalArrayObject(o: Record<string, unknown>): boolean {
  return Array.isArray(o['d']);
}

function prismaDecimalDigitsToNumber(o: {
  s?: unknown;
  e?: unknown;
  d: number[];
}): number | null {
  const sign = o.s === -1 ? -1 : 1;
  const exponent = typeof o.e === 'number' ? o.e : 0;
  const digits = o.d.map(String).join('');
  if (!digits) return 0;
  const splitAt = digits.length + exponent;
  let asString: string;
  if (splitAt <= 0) {
    asString = `0.${'0'.repeat(-splitAt)}${digits}`;
  } else if (splitAt >= digits.length) {
    asString = digits + '0'.repeat(splitAt - digits.length);
  } else {
    asString = `${digits.slice(0, splitAt)}.${digits.slice(splitAt)}`;
  }
  const n = Number.parseFloat(asString) * sign;
  return Number.isFinite(n) ? n : null;
}

function numberFromDecimalObject(o: Record<string, unknown>): number | null {
  if (isPrismaDecimalArrayObject(o)) {
    return prismaDecimalDigitsToNumber({
      s: o['s'],
      e: o['e'],
      d: o['d'] as number[],
    });
  }
  const fromString = stringFromDecimalLike(o);
  if (!fromString) return null;
  const n = Number(fromString);
  return Number.isFinite(n) ? n : null;
}

/** Parses API numbers that may be plain numbers or Prisma/decimal.js JSON (`{ s, e, d }`). */
export function numberFromDecimalLike(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    return finiteNumberFromString(value);
  }
  if (typeof value === 'object' && value !== null) {
    return numberFromDecimalObject(value as Record<string, unknown>);
  }
  return null;
}
